import { useEffect, useRef, useState } from "react";
import PortalLayout from "@/layouts/PortalLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Film, Upload, Trash2, Loader2, Edit3, Copy, X, Clock } from "lucide-react";
import { useLang } from "@/context/LangContext";

const CHUNK_SIZE = 5 * 1024 * 1024; // 5 MB
const MAX_SIZE = 500 * 1024 * 1024; // 500 MB (matches backend default)
const ALLOWED = [".mp4", ".webm", ".mov", ".mkv"];
const BACKEND = process.env.REACT_APP_BACKEND_URL;

function humanSize(bytes, t) {
  const mb = (bytes || 0) / (1024 * 1024);
  if (mb >= 1024) return `${(mb / 1024).toFixed(2)} ${t("unit_gb")}`;
  return `${mb.toFixed(1)} ${t("unit_mb")}`;
}

// Retry transient failures (network / 5xx) with backoff; 4xx errors are final.
async function postChunkWithRetry(sessionId, fd, attempts = 4) {
  for (let a = 1; ; a++) {
    try {
      return await api.post(`/upload-session/${sessionId}/chunk`, fd);
    } catch (err) {
      const status = err?.response?.status;
      if (a >= attempts || (status && status < 500)) throw err;
      await new Promise((res) => setTimeout(res, 1500 * a));
    }
  }
}

/**
 * Médiathèque personnelle — upload chunké de vidéos (MP4/WebM/MOV, max 500 Mo),
 * stockées dans Emergent Object Storage. Pas de transcodage.
 */
export default function MemberMedia() {
  const { t, lang } = useLang();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null); // {name, progress, bytes_total}
  const [editing, setEditing] = useState(null);
  const inputRef = useRef(null);

  const load = () =>
    api
      .get("/portal/videos")
      .then((r) => setVideos(r.data || []))
      .catch(() => toast.error(t("media_load_error")))
      .finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    const ext = "." + (file.name.split(".").pop() || "").toLowerCase();
    if (!ALLOWED.includes(ext)) {
      toast.error(t("media_format_unsupported").replace("{formats}", ALLOWED.join(", ")));
      return;
    }
    if (file.size > MAX_SIZE) {
      toast.error(t("media_too_large").replace("{size}", humanSize(file.size, t)).replace("{max}", humanSize(MAX_SIZE, t)));
      return;
    }
    setUploading({ name: file.name, progress: 0, bytes_total: file.size });
    try {
      // 1) Create upload session
      const fd = new FormData();
      fd.append("filename", file.name);
      fd.append("content_type", file.type || "video/mp4");
      fd.append("total_size", String(file.size));
      fd.append("title", file.name.replace(/\.[^.]+$/, ""));
      const session = (await api.post("/upload-session", fd)).data;
      const sessionId = session.session_id;

      // 2) Upload chunks sequentially (bypasses the ~100 Mo ingress limit)
      const total = Math.ceil(file.size / CHUNK_SIZE);
      for (let i = 0; i < total; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const blob = file.slice(start, end);
        const chunkFd = new FormData();
        chunkFd.append("sequence", String(i));
        chunkFd.append("chunk", blob, `chunk-${i}`);
        const r = await postChunkWithRetry(sessionId, chunkFd);
        setUploading((u) => ({ ...u, progress: r.data.progress }));
      }

      // 3) Finalize (instant: parts already in object storage)
      setUploading((u) => ({ ...u, progress: 1, finalizing: true }));
      await api.post(`/upload-session/${sessionId}/finalize`);
      toast.success(t("media_published"));
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || t("media_upload_failed"));
    } finally {
      setUploading(null);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const del = async (v) => {
    if (!window.confirm(t("media_delete_confirm").replace("{title}", v.title))) return;
    try {
      await api.delete(`/portal/videos/${v._id}`);
      toast.success(t("media_deleted"));
      load();
    } catch { toast.error(t("error_generic")); }
  };

  const copyUrl = async (v) => {
    const url = `${BACKEND}${v.url}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("media_url_copied"));
    } catch {
      toast.error(t("media_copy_failed").replace("{url}", url));
    }
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/portal/videos/${editing._id}`, {
        title: editing.title,
        description: editing.description,
      });
      toast.success(t("media_meta_saved"));
      setEditing(null);
      load();
    } catch { toast.error(t("error_generic")); }
  };

  return (
    <PortalLayout>
      <div className="mx-auto max-w-6xl px-4 py-8 lg:py-12" data-testid="member-media-page">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-serif text-3xl text-navy md:text-4xl" data-testid="media-title">{t("media_title")}</h1>
            <p className="mt-2 max-w-xl text-sm text-neutral-600">
              {t("media_intro")}
            </p>
          </div>
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-full bg-wine-500 px-5 py-2.5 text-xs font-bold text-white hover:bg-wine-600"
                 data-testid="media-upload-btn">
            <Upload className="h-3.5 w-3.5" />
            {t("media_upload_btn")}
            <input ref={inputRef} type="file" accept="video/mp4,video/webm,video/quicktime,video/x-matroska,.mp4,.webm,.mov,.mkv"
                   className="hidden"
                   onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}
                   data-testid="media-upload-input" />
          </label>
        </div>

        {/* Uploading banner */}
        {uploading && (
          <div className="mt-6 rounded-2xl border border-wine-200 bg-wine-50/50 p-4" data-testid="media-upload-progress">
            <div className="flex items-center gap-2 text-sm font-semibold text-wine-800">
              <Loader2 className="h-4 w-4 animate-spin" />
              {uploading.finalizing
                ? t("media_finalizing")
                : t("media_uploading").replace("{pct}", Math.round((uploading.progress || 0) * 100))}
            </div>
            <div className="mt-2 truncate text-xs text-wine-700/80">{uploading.name} ({humanSize(uploading.bytes_total, t)})</div>
            <div className="mt-2 h-2 overflow-hidden rounded-full bg-white">
              <div className="h-full bg-wine-500 transition-all" style={{ width: `${Math.round((uploading.progress || 0) * 100)}%` }} />
            </div>
          </div>
        )}

        <div className="mt-6 rounded-2xl bg-rose-50/40 p-4 text-xs text-wine-700/80">
          <strong>{t("media_formats_label")}</strong> MP4, WebM, MOV, MKV · <strong>{t("media_max_label")}</strong> {humanSize(MAX_SIZE, t)} ·
          <strong> {t("media_hosting_label")}</strong> {t("media_hosting_value")} · <strong>{t("media_no_transcode")}</strong> {t("media_no_transcode_hint")}
        </div>

        {/* Grid */}
        <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="media-grid">
          {loading ? (
            <div className="col-span-full py-12 text-center text-sm text-neutral-500">{t("loading")}</div>
          ) : videos.length === 0 ? (
            <div className="col-span-full rounded-2xl border-2 border-dashed border-wine-200 p-10 text-center" data-testid="media-empty">
              <Film className="mx-auto h-10 w-10 text-wine-300" />
              <div className="mt-3 font-serif text-lg text-navy">{t("media_empty_title")}</div>
              <p className="mx-auto mt-1 max-w-sm text-sm text-neutral-500">
                {t("media_empty_desc")}
              </p>
            </div>
          ) : (
            videos.map((v) => (
              <article key={v._id} className="group overflow-hidden rounded-2xl border border-neutral-200 bg-white" data-testid={`media-item-${v._id}`}>
                <div className="aspect-video bg-neutral-900">
                  {v.url && <video
                    src={`${BACKEND}${v.url}`}
                    controls
                    className="h-full w-full"
                    preload="metadata"
                  />}
                </div>
                <div className="p-4">
                  <h3 className="truncate font-semibold text-navy">{v.title}</h3>
                  {v.description && <p className="mt-1 line-clamp-2 text-xs text-neutral-600">{v.description}</p>}
                  <div className="mt-2 flex items-center gap-3 text-[11px] text-neutral-500">
                    <span>{humanSize(v.size, t)}</span>
                    <span className="inline-flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {new Date(v.created_at).toLocaleDateString(lang)}
                    </span>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    <button onClick={() => copyUrl(v)} className="inline-flex items-center gap-1 rounded-full border border-neutral-300 bg-white px-2.5 py-1 text-[11px] font-semibold text-neutral-700 hover:bg-neutral-50"
                            data-testid={`media-copy-${v._id}`}>
                      <Copy className="h-3 w-3" /> URL
                    </button>
                    <button onClick={() => setEditing({ ...v })} className="inline-flex items-center gap-1 rounded-full border border-neutral-300 bg-white px-2.5 py-1 text-[11px] font-semibold text-neutral-700 hover:bg-neutral-50"
                            data-testid={`media-edit-${v._id}`}>
                      <Edit3 className="h-3 w-3" /> {t("media_edit")}
                    </button>
                    <button onClick={() => del(v)} className="ml-auto inline-flex items-center gap-1 rounded-full border border-red-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-red-700 hover:bg-red-50"
                            data-testid={`media-delete-${v._id}`}>
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              </article>
            ))
          )}
        </div>
      </div>

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="media-edit-modal">
          <button onClick={() => setEditing(null)} className="absolute inset-0 bg-black/60 backdrop-blur-sm" aria-label={t("close")} />
          <div className="relative w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-serif text-xl text-navy">{t("media_edit_title")}</h2>
              <button onClick={() => setEditing(null)} className="grid h-8 w-8 place-items-center rounded-full text-neutral-500 hover:bg-neutral-100">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-neutral-700">{t("media_field_title")}</label>
                <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                       className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="media-edit-title" />
              </div>
              <div>
                <label className="text-xs font-semibold text-neutral-700">{t("media_field_desc")}</label>
                <textarea rows={3} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                          className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="media-edit-desc" />
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="rounded-full bg-neutral-100 px-4 py-2 text-xs font-semibold" data-testid="media-edit-cancel">{t("cancel")}</button>
              <button onClick={saveEdit} className="rounded-full bg-wine-500 px-5 py-2 text-xs font-bold text-white hover:bg-wine-600" data-testid="media-edit-save">{t("save")}</button>
            </div>
          </div>
        </div>
      )}
    </PortalLayout>
  );
}
